import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiCheckCircle, FiAlertCircle, FiTrendingUp, FiZap, FiTarget, FiGlobe, FiCode, FiImage, FiLink, FiFileText, FiBarChart2, FiDownload } from 'react-icons/fi';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const FreeSEOAuditPage: React.FC = () => {
  useDocumentTitle(
    'Free SEO Audit - Analyze Your Website Performance | Mirani Marketing',
    'Get a comprehensive free SEO audit report. Analyze technical SEO, on-page optimization, content quality, backlinks, and mobile performance. Receive actionable recommendations to improve your rankings.'
  );

  const [formData, setFormData] = useState({
    website: '',
    email: '',
    name: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('http://localhost:5000/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          service: 'Free SEO Audit Request',
          message: `SEO Audit requested for website: ${formData.website}`,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ website: '', email: '', name: '', phone: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const auditChecks = [
    {
      icon: FiCode,
      title: 'Technical SEO Analysis',
      items: [
        'Website speed and performance',
        'Mobile responsiveness check',
        'SSL certificate validation',
        'XML sitemap analysis',
        'Robots.txt review',
        'Structured data markup',
        'Core Web Vitals (LCP, FID, CLS)',
        'URL structure optimization'
      ],
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: FiFileText,
      title: 'On-Page SEO Audit',
      items: [
        'Title tag optimization',
        'Meta description quality',
        'Header tag structure (H1-H6)',
        'Keyword density analysis',
        'Internal linking structure',
        'Alt text for images',
        'Content quality assessment',
        'Duplicate content detection'
      ],
      color: 'from-green-500 to-green-600'
    },
    {
      icon: FiLink,
      title: 'Backlink Profile Analysis',
      items: [
        'Total backlinks count',
        'Domain authority metrics',
        'Toxic backlinks detection',
        'Anchor text distribution',
        'Referring domains analysis',
        'Competitor backlink gap',
        'Link velocity trends',
        'Nofollow vs dofollow ratio'
      ],
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: FiGlobe,
      title: 'Content & Keywords',
      items: [
        'Target keyword rankings',
        'Keyword cannibalization',
        'Content freshness score',
        'Readability analysis',
        'LSI keywords usage',
        'Content gap analysis',
        'Competitor content comparison',
        'Featured snippet opportunities'
      ],
      color: 'from-orange-500 to-orange-600'
    },
    {
      icon: FiImage,
      title: 'Media Optimization',
      items: [
        'Image compression check',
        'Image format analysis (WebP)',
        'Lazy loading implementation',
        'Video optimization',
        'CDN usage detection',
        'File size recommendations',
        'Alt attribute coverage',
        'Thumbnail optimization'
      ],
      color: 'from-pink-500 to-pink-600'
    },
    {
      icon: FiBarChart2,
      title: 'Analytics & Performance',
      items: [
        'Organic traffic trends',
        'Bounce rate analysis',
        'Conversion tracking setup',
        'Google Analytics integration',
        'Search Console data review',
        'Top performing pages',
        'User behavior metrics',
        'Goal completion rates'
      ],
      color: 'from-indigo-500 to-indigo-600'
    }
  ];

  const benefits = [
    {
      icon: FiCheckCircle,
      title: 'Comprehensive Report',
      description: '50+ page detailed SEO audit covering every aspect of your website\'s search performance.'
    },
    {
      icon: FiTarget,
      title: 'Actionable Insights',
      description: 'Prioritized recommendations with step-by-step instructions to fix issues immediately.'
    },
    {
      icon: FiTrendingUp,
      title: 'Competitive Analysis',
      description: 'See how you stack up against competitors and identify opportunities to outrank them.'
    },
    {
      icon: FiZap,
      title: 'Quick Wins Identified',
      description: 'Get low-hanging fruit optimizations that can boost rankings within days.'
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Submit Your Website',
      description: 'Enter your website URL and contact details in the form below.'
    },
    {
      step: '2',
      title: 'We Analyze Everything',
      description: 'Our SEO experts conduct a comprehensive audit using industry-leading tools.'
    },
    {
      step: '3',
      title: 'Receive Detailed Report',
      description: 'Get a professional PDF report within 48 hours via email with actionable recommendations.'
    },
    {
      step: '4',
      title: 'Free Consultation Call',
      description: 'Schedule a 30-minute strategy call to discuss findings and implementation roadmap.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <motion.section
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white py-20 overflow-hidden"
      >
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
                className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6"
              >
                <FiSearch className="text-4xl" />
              </motion.div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Free SEO Audit Report
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Discover exactly what's holding your website back from ranking on Google. Get a professional SEO audit report worth ₹15,000 - absolutely free.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <FiCheckCircle className="text-green-400" />
                  <span>50+ SEO Checks</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiCheckCircle className="text-green-400" />
                  <span>Delivered in 48 Hours</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiCheckCircle className="text-green-400" />
                  <span>No Credit Card Required</span>
                </div>
              </div>
            </div>

            {/* Audit Request Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Audit</h3>
              
              {submitStatus === 'success' ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                  <FiCheckCircle className="text-5xl text-green-600 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-green-900 mb-2">Request Submitted!</h4>
                  <p className="text-green-700">
                    We'll analyze your website and send a comprehensive SEO audit report to your email within 48 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Website URL *
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://yourwebsite.com"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
                    />
                  </div>

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                      <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">
                        Failed to submit. Please try again or email us at contact@miranimarketing.com
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        <FiSearch />
                        Get Free SEO Audit
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    100% free. No credit card required. Report delivered in 48 hours.
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* What We Analyze */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What We Analyze in Your SEO Audit</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our comprehensive audit examines 50+ SEO factors across 6 critical categories to identify every opportunity for improvement.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {auditChecks.map((category, index) => (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-shadow"
                >
                  <div className={`bg-gradient-to-r ${category.color} p-6`}>
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                      <category.icon className="text-3xl text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{category.title}</h3>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-3">
                      {category.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-2 text-gray-700">
                          <FiCheckCircle className="text-green-600 flex-shrink-0 mt-1" />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Get an SEO Audit?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Most websites lose 40-60% of potential traffic due to fixable SEO issues. Our audit reveals exactly what to fix.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="text-2xl text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Getting your free SEO audit is simple and takes less than 2 minutes.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {process.map((item, index) => (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-3xl font-bold text-white mb-6 mx-auto shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sample Report Preview */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">What You'll Receive</h2>
              <p className="text-xl text-white/80 max-w-3xl mx-auto">
                A professional SEO audit report with detailed analysis and prioritized recommendations.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 shadow-2xl border border-gray-700">
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-blue-400 mb-2">50+</div>
                  <div className="text-gray-400">SEO Checks</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">20+</div>
                  <div className="text-gray-400">Pages Analyzed</div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-purple-400 mb-2">100%</div>
                  <div className="text-gray-400">Actionable</div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <FiDownload className="text-2xl text-blue-400" />
                    <h3 className="text-xl font-bold">Executive Summary</h3>
                  </div>
                  <p className="text-gray-400">
                    High-level overview of your website's SEO health score, critical issues, and top opportunities.
                  </p>
                </div>

                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <FiAlertCircle className="text-2xl text-red-400" />
                    <h3 className="text-xl font-bold">Critical Issues</h3>
                  </div>
                  <p className="text-gray-400">
                    Prioritized list of problems hurting your rankings right now, with fix instructions.
                  </p>
                </div>

                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <FiTrendingUp className="text-2xl text-green-400" />
                    <h3 className="text-xl font-bold">Growth Opportunities</h3>
                  </div>
                  <p className="text-gray-400">
                    Untapped keywords, content gaps, and link building opportunities to scale traffic.
                  </p>
                </div>

                <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center gap-3 mb-3">
                    <FiBarChart2 className="text-2xl text-purple-400" />
                    <h3 className="text-xl font-bold">Competitor Analysis</h3>
                  </div>
                  <p className="text-gray-400">
                    How you compare against top 3 competitors and what they're doing better.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="text-4xl font-bold mb-6">Ready to Improve Your Rankings?</h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Join 500+ businesses who have improved their SEO with our free audit. No cost, no commitment - just actionable insights.
            </p>
            <motion.a
              href="#audit-form"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-white text-blue-600 px-10 py-5 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all"
            >
              Get Your Free SEO Audit Now
            </motion.a>
            <p className="text-sm text-white/70 mt-6">
              ✓ No credit card required ✓ Report in 48 hours ✓ 100% free forever
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FreeSEOAuditPage;
